// src/features/simulators/dispenser/components/DispenserConfigForm.tsx

import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Grid,
} from '@mui/material';
import {
  QrCode as QrIcon,
  Source as SourceIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ReservationNumberControl } from '../../shared';
import { QRCodeType, CheckInSource } from '../types';

export interface DispenserConfigValues {
  qrCodeType: QRCodeType;
  checkInSource: CheckInSource;
  performPMSCheckIn: boolean;
  reservationNumber: string;
  autoGenerateReservation: boolean;
}

interface DispenserConfigFormProps {
  values: DispenserConfigValues;
  onChange: (values: DispenserConfigValues) => void;
}

export const DispenserConfigForm: React.FC<DispenserConfigFormProps> = ({
  values,
  onChange,
}) => {
  const translate = useTranslate();

  const update = (partial: Partial<DispenserConfigValues>) => {
    onChange({ ...values, ...partial });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.dispenser.qr_type.label')}
          icon={<QrIcon />}
          variant="outlined"
        >
          <FormControl component="fieldset">
            <RadioGroup
              value={values.qrCodeType}
              onChange={(e) => update({ qrCodeType: e.target.value as QRCodeType })}
            >
              <FormControlLabel
                value={QRCodeType.RESERVATION_NUMBER}
                control={<Radio size="small" />}
                label={translate('resources.simulators.dispenser.qr_type.reservation')}
              />
              <FormControlLabel
                value={QRCodeType.ROOM_NUMBER}
                control={<Radio size="small" />}
                label={translate('resources.simulators.dispenser.qr_type.room')}
              />
            </RadioGroup>
          </FormControl>

          {values.qrCodeType === QRCodeType.RESERVATION_NUMBER && (
            <ReservationNumberControl
              value={values.reservationNumber}
              autoGenerate={values.autoGenerateReservation}
              onValueChange={(value) => update({ reservationNumber: value })}
              onAutoGenerateChange={(autoGenerate) => update({ autoGenerateReservation: autoGenerate })}
              prefix="RES"
            />
          )}
        </MyFormSection>
      </Grid>

      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.dispenser.checkin_source.label')}
          icon={<SourceIcon />}
          variant="outlined"
        >
          <FormControl component="fieldset">
            <RadioGroup
              value={values.checkInSource}
              onChange={(e) => update({ checkInSource: e.target.value as CheckInSource })}
            >
              <FormControlLabel
                value={CheckInSource.PRE_CHECKIN}
                control={<Radio size="small" />}
                label={translate('resources.simulators.dispenser.checkin_source.precheckin')}
              />
              <FormControlLabel
                value={CheckInSource.KIOSK}
                control={<Radio size="small" />}
                label={translate('resources.simulators.dispenser.checkin_source.kiosk')}
              />
              <FormControlLabel
                value={CheckInSource.DISPENSER}
                control={<Radio size="small" />}
                label={translate('resources.simulators.dispenser.checkin_source.dispenser')}
              />
            </RadioGroup>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={values.performPMSCheckIn}
                onChange={(e) => update({ performPMSCheckIn: e.target.checked })}
                disabled={values.checkInSource !== CheckInSource.DISPENSER}
                size="small"
              />
            }
            label={translate('resources.simulators.dispenser.perform_pms_checkin')}
          />
        </MyFormSection>
      </Grid>
    </Grid>
  );
};
